import {createFileRoute, useSearch} from '@tanstack/react-router';
import {useEffect, useState} from 'react';

/**
 * scorepal.co/copy-code/?code=SP-XXXXXX — where the coupon in an offer email lands. Email clients
 * run no scripts, so the copy happens here: on arrival when the browser allows it, otherwise with
 * one tap on the button. Only a code-shaped value is ever shown or copied.
 */
export const Route = createFileRoute('/copy-code')({
    component: CopyCodePage,
    head: () => ({meta: [{title: 'Your code | ScorePal'}, {name: 'robots', content: 'noindex'}]}),
});

const CODE_SHAPE = /^[A-Z0-9][A-Z0-9-]{2,39}$/;

async function writeToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch { /* fall back below */ }
    try {
        const input = document.createElement('textarea');
        input.value = text;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(input);
        return ok;
    } catch {
        return false;
    }
}

function CopyCodePage() {
    const search = useSearch({strict: false}) as {code?: unknown};
    const raw = typeof search.code === 'string' ? search.code.trim().toUpperCase() : '';
    const code = CODE_SHAPE.test(raw) ? raw : '';
    const [copied, setCopied] = useState(false);

    // Most browsers allow a copy straight after the tap that opened the page; Safari wants its own
    // tap, which the button gives.
    useEffect(() => {
        if (code) writeToClipboard(code).then(setCopied);
    }, [code]);

    const copy = async () => setCopied(await writeToClipboard(code));

    return (
        <div className="flex min-h-dvh items-center justify-center bg-white px-4 py-10">
            <div className="w-full max-w-sm text-center">
                <img src="/favicon-180x180.png" alt="ScorePal" width={56} height={56} className="mx-auto mb-6 rounded-xl" />
                {code ? (
                    <>
                        <div className="rounded-2xl border-2 border-dashed border-[#0B3DB0] bg-[#FFF8DB] px-5 py-6">
                            <div className="text-xs font-bold uppercase tracking-[.14em] text-[#0B3DB0]">&#9986; Your personal code</div>
                            <div className="my-3 select-all font-mono text-3xl font-bold tracking-[.12em] text-[#0B1B3A]">{code}</div>
                            <button type="button" onClick={copy}
                                    className={'rounded-full px-6 py-2.5 text-sm font-bold text-white transition ' + (copied ? 'bg-[#16a34a]' : 'bg-[#0B3DB0] hover:opacity-90')}>
                                {copied ? 'Copied ✓' : 'Copy code'}
                            </button>
                        </div>
                        <p className="mt-5 text-sm leading-relaxed text-[#475569]">
                            {copied ? 'Your code is copied. ' : ''}Paste it into <strong className="text-[#0B1B3A]">Have a code?</strong> when you
                            unlock your event, or on your club&rsquo;s Billing page.
                        </p>
                        <a href="/profiles/me/" className="mt-6 inline-block rounded-xl bg-[#F2C41E] px-6 py-3 text-sm font-extrabold text-[#06246E] shadow-[0_4px_0_#c9a10f]">
                            Open ScorePal →
                        </a>
                    </>
                ) : (
                    <>
                        <h1 className="text-xl font-bold text-[#0B1B3A]">That code link looks incomplete</h1>
                        <p className="mt-2 text-sm text-[#475569]">Open the email again and tap the coupon, or copy the code from it by hand.</p>
                        <a href="/" className="mt-6 inline-block text-sm font-semibold text-[#0B3DB0] underline">Go to ScorePal</a>
                    </>
                )}
            </div>
        </div>
    );
}
